import { matchPath } from 'react-router-dom';

import { USER_ROLE } from '@/config/constants';

export type TutorialEntry = {
  id: string;
  pattern: string;
  title: string;
  summary: string;
  bullets: string[];
  /** If set, only show when user role is in this list */
  roles?: USER_ROLE[];
};

/**
 * First match wins. More specific paths must appear before general prefixes.
 */
export const TUTORIAL_REGISTRY: TutorialEntry[] = [
  // —— Refinance (specific → general) ——
  {
    id: 'refinance-summary-product',
    pattern: '/refinance/summary/:productId',
    title: 'Refinance summary',
    summary: 'Compare your current loan to new scenarios and track rates.',
    bullets: [
      'Review the three advice scenarios (payment, payoff, cash-out) and primary estimate.',
      'Use rate snapshots to see how market estimates change over time.',
      'Edit intake to adjust property, loan, or program inputs.',
    ],
  },
  {
    id: 'refinance-summary',
    pattern: '/refinance/summary',
    title: 'Refinance summary',
    summary: 'Analysis and savings for your saved refinance.',
    bullets: [
      'Open a saved property from the hub or complete intake first.',
      'Guided mode highlights comparison metrics: savings, break-even, and lifetime interest.',
    ],
  },
  {
    id: 'refinance-intake-edit',
    pattern: '/refinance/intake/:productId',
    title: 'Edit refinance intake',
    summary:
      'Update loan details, statement fields, and goals for this property.',
    bullets: [
      'Upload a mortgage statement PDF when available; confirm structured fields manually.',
      'Save to push updates to your summary and admin monitoring.',
    ],
  },
  {
    id: 'refinance-intake',
    pattern: '/refinance/intake',
    title: 'Refinance intake',
    summary: 'Enter property, current loan, and new loan preferences.',
    bullets: [
      'Follow steps: statement upload (after save), loan review, then summary.',
      'Choose program and refinance type (rate/term, cash-out, etc.).',
    ],
  },
  {
    id: 'refinance-hub',
    pattern: '/refinance',
    title: 'My refinance',
    summary: 'List or jump to your refinance properties.',
    bullets: ['Start intake to add a property or open an existing refinance.'],
  },

  // —— Estimates & carts ——
  {
    id: 'loan-estimate',
    pattern: '/estimate/:productId',
    title: 'Loan estimate',
    summary: 'Interactive loan estimate for a saved property.',
    bullets: [
      'Adjust inputs to see payments, fees, and disclosures.',
      'Save preferences tied to this product where available.',
    ],
  },
  {
    id: 'profile',
    pattern: '/profile',
    title: 'Profile',
    summary:
      'Account and settings (subscription, security, email preferences).',
    bullets: [
      'Loan type, qualifications, status, and documents live under Loan in the header.',
      'The assistant bubble is hidden here to reduce clutter while you edit sensitive info.',
    ],
  },
  {
    id: 'loan',
    pattern: '/loan',
    title: 'Loan',
    summary:
      'Mortgage applications hub plus your loan profile: type, qualifications, status, and documents.',
    bullets: [
      'Loan type is the first tab (workflow, program, occupancy); then qualifications, My applications, status, and documents.',
      'The assistant bubble is hidden here while you work on loan paperwork.',
    ],
  },
  {
    id: 'qualified',
    pattern: '/qualified',
    title: 'Qualified',
    summary: 'Properties and deals in your qualified pipeline.',
    bullets: [
      'Move items toward contract as requirements are met.',
      'Use the main cart for earlier-stage shopping.',
    ],
  },
  {
    id: 'contract-cart',
    pattern: '/contract',
    title: 'Contract',
    summary: 'Contracts ready to start or continue.',
    bullets: ['Open a row to view contract detail, negotiation, or downloads.'],
  },
  {
    id: 'main',
    pattern: '/main',
    title: 'Main',
    summary: 'Your primary property / estimate list.',
    bullets: ['Add or open properties to run estimates and start contracts.'],
  },

  // —— Properties / Deal Room ——
  {
    id: 'deal-room-stage',
    pattern: '/properties/:propertyId/stage/:stageNum',
    title: 'Deal Room stage',
    summary: 'Work through this stage of your purchase or refinance pipeline.',
    bullets: [
      'Use the stage pills to jump between saved, qualified, offer, and closing steps.',
      'Complete checklist items shown for this stage before moving forward.',
      'Turn Guided mode off anytime if you prefer a cleaner view.',
    ],
  },
  {
    id: 'deal-room',
    pattern: '/properties/:propertyId',
    title: 'Deal Room',
    summary:
      'Your workspace for rates, payments, and next steps on this property.',
    bullets: [
      'Adjust loan program and down payment to see live payment estimates.',
      'Review Unbeatable Credit from the buyer-agent commission on this listing.',
      'Follow the stage sidebar from saved → qualified → offer → close.',
    ],
  },
  {
    id: 'properties-grid',
    pattern: '/properties',
    title: 'My Properties',
    summary: 'Saved listings with rates, payments, and pipeline progress.',
    bullets: [
      'Open a card to enter the Deal Room for that address.',
      'Filter by purchase, refinance, or investment; sort by stage or price.',
      'Add a property manually or install the Chrome extension to import listings.',
    ],
  },

  // —— Buyer contract flow ——
  {
    id: 'buyer-negotiation-clauses',
    pattern: '/contract/negotiation-clauses/:contractId',
    title: 'Negotiation clauses',
    summary: 'Review and work through clause-level negotiation.',
    bullets: [
      'Focus on one clause at a time; status updates for both parties.',
      'Coordinate with your agent on strategy before accepting changes.',
    ],
  },
  {
    id: 'contract-download',
    pattern: '/contract/download/:contractId',
    title: 'Download contract',
    summary: 'Export or download contract documents.',
    bullets: ['Verify the package is complete before sharing outside the app.'],
  },
  {
    id: 'contract-detail',
    pattern: '/contract/:productId',
    title: 'Contract detail',
    summary: 'Deep dive on a contract tied to a product.',
    bullets: [
      'See status, parties, and next actions for this deal.',
      'Navigate to negotiation or post-agreement tools from here when enabled.',
    ],
  },
  {
    id: 'view-contract',
    pattern: '/view-contract/:contractId',
    title: 'View contract',
    summary: 'Read-only or simplified contract view.',
    bullets: [
      'Use toolbar actions for print, download, or next steps if shown.',
    ],
  },
  {
    id: 'buyer-negotiate',
    pattern: '/buyer/negotiate/:contractId',
    title: 'Buyer negotiation',
    summary: 'Negotiate terms as the buyer.',
    bullets: [
      'Propose changes and track seller or agent responses.',
      'Document center may list required uploads for this stage.',
    ],
  },
  {
    id: 'buyer-property-dashboard',
    pattern: '/buyer/property-dashboard/:contractId',
    title: 'Buyer post-agreement dashboard',
    summary: 'Tasks and milestones after agreement.',
    bullets: [
      'Track inspections, financing, title, and closing checklist items.',
      'Upload or review documents requested for this property.',
    ],
  },

  // —— Agent ——
  {
    id: 'agent-qualified-contract',
    pattern: '/agent/qualified-contract/:agentId',
    title: 'Agent qualified contracts',
    summary: 'Qualified pipeline for your agent account.',
    bullets: [
      'Advance deals toward ratified contract and post-agreement work.',
    ],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-smart-router',
    pattern: '/agent/smart-router/:agentId',
    title: 'Agent smart router',
    summary: 'Routing hub for agent workflows.',
    bullets: [
      'Use shortcuts to dashboard, products, or contracts as configured.',
    ],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-dashboard',
    pattern: '/agent/dashboard/:agentId',
    title: 'Agent dashboard',
    summary: 'Overview of your listings and buyer activity.',
    bullets: [
      'Jump to negotiation, counter-proposals, or property dashboards.',
    ],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-product',
    pattern: '/agent/product/:productId',
    title: 'Agent product detail',
    summary: 'Manage a property/estimate from the agent side.',
    bullets: ['Link or invite parties; align on pricing and contract status.'],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-property-dashboard',
    pattern: '/agent/property-dashboard/:contractId',
    title: 'Agent property dashboard',
    summary: 'Post-agreement coordination for this contract.',
    bullets: ['Monitor tasks, documents, and party progress toward closing.'],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-counter-proposal',
    pattern: '/agent/counter-proposal/:contractId',
    title: 'Counter-proposal',
    summary: 'Draft or respond to a counter-offer.',
    bullets: ['Save drafts; history is available from the history route.'],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-negotiate-contract',
    pattern: '/agent/contract/:uid',
    title: 'Agent negotiate contract',
    summary: 'Negotiate on behalf of clients.',
    bullets: [
      'Keep clauses aligned with your brokerage and local requirements.',
    ],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'counter-proposal-history',
    pattern: '/contract/counter-proposal-history/:contractId',
    title: 'Counter-proposal history',
    summary: 'Timeline of counters for this contract.',
    bullets: ['Compare versions before accepting or countering again.'],
    roles: [USER_ROLE.SELLER_AGENT],
  },
  {
    id: 'agent-add-contract',
    pattern: '/contract/agent/:uid',
    title: 'Agent contract setup',
    summary: 'Add or edit contract details as agent.',
    bullets: ['Complete required fields before sending to buyer or seller.'],
    roles: [USER_ROLE.SELLER_AGENT],
  },

  // —— Seller ——
  {
    id: 'seller-sign',
    pattern: '/seller/contract/:contractId/sign',
    title: 'Sign contract',
    summary: 'Seller signing step after negotiation.',
    bullets: [
      'Review the full PDF; confirm identity and intent before signing.',
    ],
    roles: [USER_ROLE.SELLER],
  },
  {
    id: 'seller-property-dashboard',
    pattern: '/seller/property-dashboard/:contractId',
    title: 'Seller post-agreement dashboard',
    summary: 'Seller-side tasks after agreement.',
    bullets: [
      'Disclosures, access for inspections, and closing prep items appear here.',
    ],
    roles: [USER_ROLE.SELLER],
  },
  {
    id: 'seller-negotiate',
    pattern: '/seller/negotiate/:contractId',
    title: 'Seller negotiation',
    summary: 'Respond to buyer proposals.',
    bullets: ['Counter or accept; your agent may advise on strategy.'],
    roles: [USER_ROLE.SELLER],
  },
  {
    id: 'seller-add-detail',
    pattern: '/seller/add-detail/:contractId',
    title: 'Seller contract detail',
    summary: 'Provide seller-specific contract information.',
    bullets: ['Fill required fields so the contract can progress.'],
    roles: [USER_ROLE.SELLER],
  },
  {
    id: 'seller-product',
    pattern: '/seller/product/:productId',
    title: 'Seller product',
    summary: 'Your listing / property workspace.',
    bullets: ['Syncs with buyer and agent views for this property.'],
    roles: [USER_ROLE.SELLER],
  },
  {
    id: 'seller-dashboard',
    pattern: '/seller/dashboard/:sellerId',
    title: 'Seller dashboard',
    summary: 'Overview of your selling activity.',
    bullets: ['Open contracts, negotiations, and property tools from here.'],
    roles: [USER_ROLE.SELLER],
  },
  {
    id: 'seller-main',
    pattern: '/seller/main/:sellerId',
    title: 'Seller main',
    summary: 'Seller home hub.',
    bullets: ['Navigate to dashboard, products, or active deals.'],
    roles: [USER_ROLE.SELLER],
  },

  // —— Admin (specific first) ——
  {
    id: 'admin-email-notifications',
    pattern: '/admin/email/notifications',
    title: 'Email notifications',
    summary: 'Configure or monitor outbound email notifications.',
    bullets: ['Use for operational visibility on buyer/seller communications.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-product-detail',
    pattern: '/admin/product/:productId',
    title: 'Estimate detail (admin)',
    summary: 'Inspect a product/estimate as user.',
    bullets: ['Review loan assumptions, address, and linked contract if any.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-property-dashboard',
    pattern: '/admin/property/:contractId',
    title: 'Admin property dashboard',
    summary: 'Operational view of a contract/property.',
    bullets: ['Cross-check status with pipeline and document tools.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-debug',
    pattern: '/admin/debug',
    title: 'Admin debug',
    summary: 'Internal debugging utilities.',
    bullets: [
      'For support and engineering; avoid changing data without a ticket.',
    ],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-user',
    pattern: '/admin/user',
    title: 'User pipeline',
    summary: 'Manage users and qualification pipeline.',
    bullets: ['Search, filter, and update user records.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-documents',
    pattern: '/admin/documents',
    title: 'Document management',
    summary: 'Cross-contract document oversight.',
    bullets: ['Review uploads and statuses tied to deals.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-agent',
    pattern: '/admin/agent',
    title: 'Agent assignment',
    summary: 'Assign or audit agent relationships.',
    bullets: ['Keep agent links accurate for routing and permissions.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-email',
    pattern: '/admin/email',
    title: 'Manage emails',
    summary: 'Email templates and sends.',
    bullets: ['Coordinate with notifications for consistent messaging.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-feedback',
    pattern: '/admin/feedback',
    title: 'Messenger / feedback',
    summary: 'Internal messaging and feedback queue.',
    bullets: ['Triage user issues and route to the right owner.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-interest-rates',
    pattern: '/admin/interest-rates',
    title: 'Interest rates',
    summary: 'Configure rate matrices and assumptions.',
    bullets: ['Changes affect live estimates across the app.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-properties',
    pattern: '/admin/properties',
    title: 'Contract management',
    summary: 'List of properties/contracts for admin review.',
    bullets: ['Drill into a row for the property dashboard.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-product-list',
    pattern: '/admin/product',
    title: 'Estimate management',
    summary: 'All saved estimates/products.',
    bullets: ['Edit metadata, owner, or open detail for support.'],
    roles: [USER_ROLE.ADMIN],
  },
  {
    id: 'admin-dashboard',
    pattern: '/admin',
    title: 'Admin dashboard',
    summary: 'Administrative home.',
    bullets: [
      'Jump to users, documents, rates, or contracts from the header icons.',
    ],
    roles: [USER_ROLE.ADMIN],
  },

  // —— Public & verify ——
  {
    id: 'verify-delete',
    pattern: '/verify/delete-request',
    title: 'Verify delete request',
    summary: 'Confirm account deletion flow.',
    bullets: ['Follow emailed instructions; this page completes verification.'],
  },
  {
    id: 'verify-signin',
    pattern: '/verify/signin',
    title: 'Verify sign-in',
    summary: 'Complete a secure sign-in or magic link step.',
    bullets: ['If the link expired, request a new one from the login screen.'],
  },
  {
    id: 'verify-security',
    pattern: '/verify/security',
    title: 'Verify security',
    summary: 'Security verification challenge.',
    bullets: ['Complete any prompts to restore access to your account.'],
  },
  {
    id: 'chat-demo',
    pattern: '/test/chat-demo',
    title: 'AI chat demo',
    summary: 'Sandbox for the assistant UI.',
    bullets: ['For demos only; not tied to your real loan data.'],
  },
  {
    id: 'hybrid-rates-test',
    pattern: '/test/hybrid-rates',
    title: 'Hybrid rates test',
    summary: 'Engineering test for hybrid interest rate behavior.',
    bullets: ['Safe to ignore unless you are validating rate logic.'],
  },
  {
    id: 'terms',
    pattern: '/terms-of-service',
    title: 'Terms of service',
    summary: 'Legal terms for using Unbeatable.',
    bullets: ['Read before relying on estimates or contracts in production.'],
  },
  {
    id: 'privacy',
    pattern: '/privacy-policy',
    title: 'Privacy policy',
    summary: 'How we handle personal data.',
    bullets: ['Contact support if you need a data export or deletion.'],
  },
  {
    id: 'data-request',
    pattern: '/data-request',
    title: 'Data request',
    summary: 'Privacy-related data requests.',
    bullets: [
      'Submit the form for access, correction, or deletion where applicable.',
    ],
  },
  {
    id: 'support',
    pattern: '/support',
    title: 'Support',
    summary: 'Get help from the team.',
    bullets: ['Include screenshots and property/contract IDs when possible.'],
  },
  {
    id: 'pricing',
    pattern: '/pricing',
    title: 'Pricing',
    summary: 'Plans and pricing overview.',
    bullets: [
      'Compare tiers before upgrading; sign in to manage subscription in profile.',
    ],
  },
  {
    id: 'landing',
    pattern: '/',
    title: 'Home',
    summary: 'Welcome to Unbeatable Loans.',
    bullets: [
      'Sign in to access estimates, contracts, and refinance tools.',
      'Use Guided mode anytime for tips on the page you are viewing.',
    ],
  },
];

export function getTutorialForPath(
  pathname: string,
  role: USER_ROLE | string | null | undefined
): TutorialEntry | null {
  const normalized =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname;

  for (const entry of TUTORIAL_REGISTRY) {
    const match = matchPath(
      { path: entry.pattern, end: true, caseSensitive: false },
      normalized
    );
    if (!match) continue;

    if (entry.roles?.length) {
      const r = role as USER_ROLE | undefined;
      if (!r || !entry.roles.includes(r)) continue;
    }
    return entry;
  }
  return null;
}
